<template>
  <div class="category-report-attributes-section">
    <h4>{{ t('Atributos de Laudo') }}</h4>
    <div class="steps-list">
      <div
        v-for="(attr, index) in attributes"
        :key="attr._key"
        class="step-row"
      >
        <span class="step-order">{{ index + 1 }}.</span>
        <select-list
          :elem-id="elemIdPrefix + '_' + index"
          v-model="attr.attribute_id"
          :itens-select="attributeOptions"
          :placeholder="t('Selecione um atributo de laudo')"
          :auto-select-first="false"
          :data-testid="`so-category-report-attributes-select-attr-${index}`"
          style="flex: 1;"
        />
        <select-list
          :elem-id="elemIdPrefix + '_type_' + index"
          v-model="attr.report_type"
          :itens-select="reportTypeOptions"
          :placeholder="t('Tipo')"
          :auto-select-first="false"
          :data-testid="`so-category-report-attributes-select-type-${index}`"
          style="width: 140px;"
        />
        <label class="required-toggle">
          <input
            :data-testid="`${elemIdPrefix}_required_${index}`"
            type="checkbox"
            v-model="attr.required"
          />
          {{ t('Obrigatório') }}
        </label>
        <Button
          icon="pi pi-trash"
          severity="danger"
          text
          rounded
          size="small"
          :data-testid="`so-category-report-attributes-delete-btn-${index}`"
          @click="removeAttribute(index)"
        />
      </div>
    </div>
    <Button
      :label="t('Adicionar Atributo de Laudo')"
      icon="pi pi-plus"
      severity="secondary"
      size="small"
      outlined
      data-testid="so-category-report-attributes-add-btn"
      @click="addAttribute"
      style="margin-top: 8px;"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOReportAttributes } from '../../composables/useSOReportAttributes';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface AttributeOption {
  ID: string;
  Name: string;
}

interface AttributeRow {
  attribute_id: string | null;
  report_type: string;
  required: boolean;
  _key: number;
}

interface FetchedReportAttribute {
  so_ra_id: Id;
  so_ra_name: string;
}

interface CategoryReportAttributeBinding {
  so_cra_attribute_id?: Id;
  attribute_id?: Id;
  so_cra_report_type?: string;
  report_type?: string;
  so_cra_required?: number | string;
  required?: number | string | boolean;
}

interface Props {
  categoryId: Id;
  elemIdPrefix?: string;
}

const props = withDefaults(defineProps<Props>(), {
  elemIdPrefix: 'soCategoryReportAttr'
});

const { t } = useI18n();
const { fetchAll, fetchCategoryAttributes, saveCategoryAttributes } = useSOReportAttributes();

const attributeOptions = ref<AttributeOption[]>([]);
const reportTypeOptions = ref<AttributeOption[]>([
  { ID: 'entry', Name: t('Entrada') },
  { ID: 'exit', Name: t('Saída') }
]);
const attributes = ref<AttributeRow[]>([]);
let keyCounter = 0;

const loadOptions = async (): Promise<void> => {
  try {
    const fetched = await fetchAll() as FetchedReportAttribute[];
    attributeOptions.value = fetched.map((item) => ({
      ID: String(item.so_ra_id),
      Name: item.so_ra_name
    }));
  } catch (err: unknown) {
    logError('SOCategoryReportAttributesPicker', 'loadOptions', 'Error', err);
    attributeOptions.value = [];
  }
  eventBus.emit('so-category-report-attributes-picker.options-loaded', { count: attributeOptions.value.length });
};

const loadAttributes = async (): Promise<void> => {
  try {
    const bindings = await fetchCategoryAttributes(Number(props.categoryId)) as CategoryReportAttributeBinding[];
    attributes.value = bindings.map((b) => ({
      attribute_id: String(b.so_cra_attribute_id || b.attribute_id),
      report_type: b.so_cra_report_type || b.report_type || 'entry',
      required: b.so_cra_required === 1 || b.so_cra_required === '1' || b.required === 1 || b.required === '1',
      _key: ++keyCounter
    }));
  } catch (err: unknown) {
    logError('SOCategoryReportAttributesPicker', 'loadAttributes', 'Error', err);
    attributes.value = [];
  }
};

const addAttribute = (): void => {
  attributes.value.push({
    attribute_id: null,
    report_type: 'entry',
    required: false,
    _key: ++keyCounter
  });
  eventBus.emit('so-category-report-attributes-picker.row-added', { count: attributes.value.length });
};

const removeAttribute = (index: number): void => {
  attributes.value.splice(index, 1);
};

const saveAttributes = async (): Promise<void> => {
  const payload = attributes.value
    .filter((a) => a.attribute_id)
    .map((a, i) => ({
      attribute_id: Number(a.attribute_id),
      report_type: a.report_type || 'entry',
      required: a.required ? 1 : 0,
      display_order: i + 1
    }));
  log('SOCategoryReportAttributesPicker', 'saveAttributes', 'Payload', { categoryId: props.categoryId, payload, raw: attributes.value });
  await saveCategoryAttributes(props.categoryId, payload);
};

onMounted(async () => {
  log('SOCategoryReportAttributesPicker', 'onMounted', 'Loading', { categoryId: props.categoryId });
  await loadOptions();
  await loadAttributes();
});

defineExpose({ saveAttributes, loadAttributes });
</script>

<style scoped>
.category-report-attributes-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.category-report-attributes-section h4 {
  margin-bottom: 12px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 2px solid transparent;
  background: var(--p-surface-0);
}

.step-order {
  font-weight: 600;
  min-width: 24px;
  color: var(--p-text-muted-color);
}

.required-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
  cursor: pointer;
}
</style>
